import { debug } from '../app';

export type ContentSelectors = { 
    header: string,
    footer: string, 
    content: string, 
};

export class AjaxHistory extends EventTarget {
    private timeout: number;
    private contentSelectors: ContentSelectors;

    constructor(contentSelectors: ContentSelectors, timeout: number = 500) {
        super();

        this.contentSelectors = contentSelectors;
        this.timeout = timeout;
        this.init();
    }

    private fetchPage = (url: string): void => {
        const event = new CustomEvent('fetchingPage', { detail: { url } });
        this.dispatchEvent(event);

        const delayPromise = new Promise(resolve => setTimeout(resolve, this.timeout));

        const xhrPromise = new Promise((resolve, reject) => {
            const xhr: XMLHttpRequest = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    
            xhr.onload = () => {
                if (xhr.status >= 200) {
                    resolve(xhr.responseText);
                } else {
                    reject(new Error(`Failed to load ${url}`));
                }
            };
    
            xhr.onerror = () => {
                reject(new Error(`Network error while loading ${url}`));
            };
    
            xhr.send();
        });

        Promise.all([delayPromise, xhrPromise]).then(([_, responseText]) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(responseText as string, 'text/html');

            const newContent = doc.querySelector(this.contentSelectors.content);
            const oldContent = document.querySelector(this.contentSelectors.content);
            oldContent.innerHTML = newContent.innerHTML;
            this.listenToClick(document.querySelector(this.contentSelectors.content));

            const newHeader = doc.querySelector(this.contentSelectors.header);
            const oldHeader = document.querySelector(this.contentSelectors.header);
            oldHeader.innerHTML = newHeader.innerHTML;
            this.listenToClick(document.querySelector(this.contentSelectors.header));

            const newFooter = doc.querySelector(this.contentSelectors.footer);
            const oldFooter = document.querySelector(this.contentSelectors.footer);
            oldFooter.innerHTML = newFooter.innerHTML;
            this.listenToClick(document.querySelector(this.contentSelectors.footer));

            const newBodyClass = doc.querySelector('body').className;
            document.querySelector('body').className = newBodyClass;

            document.title = doc.title;
            history.pushState(null, '', url);

            const event = new CustomEvent('pageFetched', { detail: { url } });
            this.dispatchEvent(event);
        }).catch(error => {
            console.error('Error in fetchPage:', error);
        });
    }

    private listenToPopState = (): void => {
        window.addEventListener('popstate', (event) => {
            debug ? console.log('Listening to popstate', event) : null;
            this.fetchPage(location.pathname);
        });
    }

    private listenToClick = (target: HTMLElement): void => {
        const links = target.querySelectorAll('a');

        links.forEach((link) => {
            if (link.attributes.getNamedItem('target') === null) {
                link.addEventListener('click', (event) => {
                    const href = link.attributes.getNamedItem('href').value as string;
                    event.preventDefault();
                    if (!href.startsWith('/')) {
                        return;
                    }  
                    debug ? console.log('Listening to click') : null;
                    this.fetchPage(href);
                });
            }
        });
    }

    private init = (): void => {
        debug ? console.log('AjaxHistory initialized', this.contentSelectors) : null;
        this.listenToPopState();
        this.listenToClick(document.querySelector('body'));
    }
}